/**
 * Kitchen Template Library (BYK-140)
 *
 * Provides pre-built prompt configurations optimized for Gemini 3 Pro Image API.
 * Implements hierarchical reasoning: subject → style → camera → lighting.
 *
 * Based on Gemini 3 Pro Image documentation 2025:
 * - Native resolutions: 1K (1024), 2K (2048), 4K (4096)
 * - Prompt structure matters for spatial reasoning
 */

import { z } from "zod";

// =============================================================================
// Schema Definitions
// =============================================================================

export const TemplateCategorySchema = z.enum([
  "modern_minimalist",
  "industrial",
  "mediterranean",
  "scandinavian",
  "traditional",
  "kosher_family",
  "compact",
  "luxury",
]);

export const CameraPresetSchema = z.enum([
  "front",
  "corner",
  "top_down",
  "eye_level",
  "wide_angle",
  "close_up",
]);

export const LightingMoodSchema = z.enum([
  "daylight",
  "golden_hour",
  "evening",
  "studio",
  "dramatic",
  "soft_ambient",
]);

export const StyleModifierSchema = z.object({
  id: z.string(),
  name: z.string(),
  prompt: z.string(),
  category: z.enum(["material", "atmosphere", "detail", "composition"]),
  isDefault: z.boolean().default(false),
});

export const CameraPresetConfigSchema = z.object({
  id: CameraPresetSchema,
  name: z.string(),
  prompt: z.string(),
  fov: z.number().optional(), // Field of view hint
  height: z.string().optional(), // e.g., "eye level", "2 meters"
});

export const LightingConfigSchema = z.object({
  id: LightingMoodSchema,
  name: z.string(),
  prompt: z.string(),
  timeOfDay: z.string().optional(),
  shadows: z.enum(["soft", "medium", "hard"]).optional(),
});

export const KashrutConfigSchema = z.object({
  enabled: z.boolean(),
  meatZonePrompt: z.string(),
  dairyZonePrompt: z.string(),
  separationDistance: z.number(), // cm
});

export const KitchenTemplateSchema = z.object({
  id: z.string(),
  category: TemplateCategorySchema,
  name: z.string(),
  description: z.string(),
  thumbnail: z.string().optional(), // R2 key
  basePrompt: z.string(),
  styleModifiers: z.array(StyleModifierSchema),
  cameraPresets: z.array(CameraPresetConfigSchema),
  lightingConfig: z.array(LightingConfigSchema),
  kashrut: KashrutConfigSchema.optional(),
  tags: z.array(z.string()).optional(),
});

export const RenderResolutionSchema = z.enum(["1K", "2K", "4K"]);

export const AspectRatioSchema = z.enum([
  "1:1",
  "4:3",
  "16:9",
  "3:2",
  "21:9",
]);

// =============================================================================
// Type Exports
// =============================================================================

export type TemplateCategory = z.infer<typeof TemplateCategorySchema>;
export type CameraPreset = z.infer<typeof CameraPresetSchema>;
export type LightingMood = z.infer<typeof LightingMoodSchema>;
export type StyleModifier = z.infer<typeof StyleModifierSchema>;
export type CameraPresetConfig = z.infer<typeof CameraPresetConfigSchema>;
export type LightingConfig = z.infer<typeof LightingConfigSchema>;
export type KashrutConfig = z.infer<typeof KashrutConfigSchema>;
export type KitchenTemplate = z.infer<typeof KitchenTemplateSchema>;
export type RenderResolution = z.infer<typeof RenderResolutionSchema>;
export type AspectRatio = z.infer<typeof AspectRatioSchema>;

// =============================================================================
// Resolution Configuration
// =============================================================================

export const RESOLUTION_CONFIG: Record<RenderResolution, { width: number; height: Record<AspectRatio, number> }> = {
  "1K": {
    width: 1024,
    height: {
      "1:1": 1024,
      "4:3": 768,
      "16:9": 576,
      "3:2": 683,
      "21:9": 439,
    },
  },
  "2K": {
    width: 2048,
    height: {
      "1:1": 2048,
      "4:3": 1536,
      "16:9": 1152,
      "3:2": 1365,
      "21:9": 878,
    },
  },
  "4K": {
    width: 4096,
    height: {
      "1:1": 4096,
      "4:3": 3072,
      "16:9": 2304,
      "3:2": 2731,
      "21:9": 1756,
    },
  },
};

// =============================================================================
// Shared Style Modifiers
// =============================================================================

export const COMMON_STYLE_MODIFIERS: StyleModifier[] = [
  // Material modifiers
  {
    id: "natural_materials",
    name: "Natural Materials",
    prompt: "featuring natural wood grain, stone textures, and organic materials",
    category: "material",
    isDefault: false,
  },
  {
    id: "high_gloss",
    name: "High Gloss",
    prompt: "with high-gloss lacquered surfaces, reflective finishes",
    category: "material",
    isDefault: false,
  },
  {
    id: "matte_finish",
    name: "Matte Finish",
    prompt: "with matte, fingerprint-resistant surfaces and soft-touch finishes",
    category: "material",
    isDefault: false,
  },
  {
    id: "mixed_metals",
    name: "Mixed Metals",
    prompt: "combining brass, copper, and stainless steel accents",
    category: "material",
    isDefault: false,
  },
  // Atmosphere modifiers
  {
    id: "cozy_warm",
    name: "Cozy & Warm",
    prompt: "warm and inviting atmosphere with soft lighting",
    category: "atmosphere",
    isDefault: false,
  },
  {
    id: "bright_airy",
    name: "Bright & Airy",
    prompt: "bright, airy space with abundant natural light",
    category: "atmosphere",
    isDefault: false,
  },
  {
    id: "moody_dramatic",
    name: "Moody & Dramatic",
    prompt: "dramatic atmosphere with deep shadows and focused lighting",
    category: "atmosphere",
    isDefault: false,
  },
  // Detail modifiers
  {
    id: "plants_greenery",
    name: "Plants & Greenery",
    prompt: "with potted herbs, hanging plants, and natural greenery",
    category: "detail",
    isDefault: false,
  },
  {
    id: "styled_decor",
    name: "Styled Decor",
    prompt: "with carefully styled accessories, cookbooks, and decorative items",
    category: "detail",
    isDefault: false,
  },
  {
    id: "minimal_clean",
    name: "Minimal & Clean",
    prompt: "clean, uncluttered countertops with minimal accessories",
    category: "detail",
    isDefault: true,
  },
  // Composition modifiers
  {
    id: "architectural_focus",
    name: "Architectural Focus",
    prompt: "emphasizing architectural details and spatial design",
    category: "composition",
    isDefault: false,
  },
  {
    id: "lifestyle_moment",
    name: "Lifestyle Moment",
    prompt: "capturing a lived-in moment with coffee cups and fresh ingredients",
    category: "composition",
    isDefault: false,
  },
];

// =============================================================================
// Shared Camera Presets
// =============================================================================

export const COMMON_CAMERA_PRESETS: CameraPresetConfig[] = [
  {
    id: "front",
    name: "Front View",
    prompt: "straight-on frontal view of the kitchen, centered composition",
    fov: 50,
    height: "eye level",
  },
  {
    id: "corner",
    name: "Corner View",
    prompt: "three-quarter angle view from corner, showing depth and dimension",
    fov: 60,
    height: "eye level",
  },
  {
    id: "top_down",
    name: "Top Down",
    prompt: "bird's eye view from above, showing full layout",
    fov: 45,
    height: "overhead",
  },
  {
    id: "eye_level",
    name: "Eye Level",
    prompt: "natural eye-level perspective at 160cm height, realistic viewpoint",
    fov: 55,
    height: "160cm",
  },
  {
    id: "wide_angle",
    name: "Wide Angle",
    prompt: "wide-angle shot capturing the entire space, slight perspective distortion",
    fov: 90,
    height: "eye level",
  },
  {
    id: "close_up",
    name: "Close-Up Detail",
    prompt: "close-up detail shot focusing on specific features and textures",
    fov: 35,
    height: "counter level",
  },
];

// =============================================================================
// Shared Lighting Configs
// =============================================================================

export const COMMON_LIGHTING_CONFIGS: LightingConfig[] = [
  {
    id: "daylight",
    name: "Natural Daylight",
    prompt: "bright natural daylight streaming through windows, soft shadows",
    timeOfDay: "midday",
    shadows: "soft",
  },
  {
    id: "golden_hour",
    name: "Golden Hour",
    prompt: "warm golden hour sunlight, long shadows, amber tones",
    timeOfDay: "late afternoon",
    shadows: "medium",
  },
  {
    id: "evening",
    name: "Evening Ambiance",
    prompt: "warm evening lighting with pendant lights and under-cabinet LEDs",
    timeOfDay: "evening",
    shadows: "soft",
  },
  {
    id: "studio",
    name: "Studio Lighting",
    prompt: "professional studio lighting, even illumination, controlled highlights",
    shadows: "soft",
  },
  {
    id: "dramatic",
    name: "Dramatic",
    prompt: "dramatic lighting with strong contrasts, focused spotlights, deep shadows",
    shadows: "hard",
  },
  {
    id: "soft_ambient",
    name: "Soft Ambient",
    prompt: "soft diffused ambient lighting, no harsh shadows, even exposure",
    shadows: "soft",
  },
];

// =============================================================================
// Kitchen Templates (8 Categories)
// =============================================================================

export const KITCHEN_TEMPLATES: KitchenTemplate[] = [
  // 1. Modern Minimalist
  {
    id: "modern_minimalist",
    category: "modern_minimalist",
    name: "Modern Minimalist",
    description: "Clean lines, handleless cabinets, and seamless surfaces with a focus on functionality",
    basePrompt:
      "Ultra-modern minimalist kitchen design with handleless flat-panel cabinets, " +
      "integrated appliances, waterfall countertops, clean geometric lines, " +
      "hidden storage solutions, and a sophisticated neutral color palette. " +
      "Photorealistic architectural rendering, 8K quality, professional interior photography.",
    styleModifiers: [
      ...COMMON_STYLE_MODIFIERS.filter((m) =>
        ["matte_finish", "minimal_clean", "bright_airy", "architectural_focus"].includes(m.id)
      ),
      {
        id: "monochromatic",
        name: "Monochromatic",
        prompt: "monochromatic color scheme with subtle tonal variations",
        category: "material",
        isDefault: true,
      },
    ],
    cameraPresets: COMMON_CAMERA_PRESETS,
    lightingConfig: COMMON_LIGHTING_CONFIGS,
    tags: ["modern", "minimalist", "contemporary", "handleless"],
  },

  // 2. Industrial
  {
    id: "industrial",
    category: "industrial",
    name: "Industrial Loft",
    description: "Exposed brick, metal accents, and raw materials with urban character",
    basePrompt:
      "Industrial loft kitchen with exposed brick walls, black steel frame elements, " +
      "open shelving with pipe brackets, concrete countertops, vintage Edison bulb lighting, " +
      "stainless steel appliances, and reclaimed wood accents. " +
      "Urban warehouse aesthetic, photorealistic rendering, professional photography.",
    styleModifiers: [
      ...COMMON_STYLE_MODIFIERS.filter((m) =>
        ["mixed_metals", "moody_dramatic", "styled_decor"].includes(m.id)
      ),
      {
        id: "exposed_elements",
        name: "Exposed Elements",
        prompt: "exposed ductwork, pipes, and structural elements as design features",
        category: "detail",
        isDefault: true,
      },
      {
        id: "vintage_industrial",
        name: "Vintage Industrial",
        prompt: "vintage industrial equipment and factory-inspired fixtures",
        category: "material",
        isDefault: false,
      },
    ],
    cameraPresets: COMMON_CAMERA_PRESETS,
    lightingConfig: COMMON_LIGHTING_CONFIGS,
    tags: ["industrial", "loft", "urban", "exposed brick"],
  },

  // 3. Mediterranean
  {
    id: "mediterranean",
    category: "mediterranean",
    name: "Mediterranean Villa",
    description: "Warm terracotta, hand-painted tiles, and rustic elegance",
    basePrompt:
      "Mediterranean villa kitchen with hand-painted ceramic tiles, terracotta flooring, " +
      "arched doorways, warm wood cabinets with ornate details, wrought iron accents, " +
      "copper cookware displays, and olive branch arrangements. " +
      "Tuscan warmth, photorealistic rendering, natural lighting.",
    styleModifiers: [
      ...COMMON_STYLE_MODIFIERS.filter((m) =>
        ["natural_materials", "cozy_warm", "plants_greenery", "lifestyle_moment"].includes(m.id)
      ),
      {
        id: "artisan_tiles",
        name: "Artisan Tiles",
        prompt: "hand-painted artisan tiles with traditional Mediterranean patterns",
        category: "material",
        isDefault: true,
      },
      {
        id: "rustic_charm",
        name: "Rustic Charm",
        prompt: "rustic wooden beams, aged patina, and weathered textures",
        category: "atmosphere",
        isDefault: false,
      },
    ],
    cameraPresets: COMMON_CAMERA_PRESETS,
    lightingConfig: COMMON_LIGHTING_CONFIGS,
    tags: ["mediterranean", "tuscan", "rustic", "warm"],
  },

  // 4. Scandinavian
  {
    id: "scandinavian",
    category: "scandinavian",
    name: "Scandinavian Nordic",
    description: "Light wood, white surfaces, and hygge-inspired coziness",
    basePrompt:
      "Scandinavian Nordic kitchen with light oak wood cabinets, white quartz countertops, " +
      "pale gray backsplash, minimalist black hardware, large windows maximizing natural light, " +
      "woven textile accents, and simple functional design. " +
      "Hygge atmosphere, clean aesthetic, photorealistic interior photography.",
    styleModifiers: [
      ...COMMON_STYLE_MODIFIERS.filter((m) =>
        ["natural_materials", "bright_airy", "minimal_clean", "plants_greenery"].includes(m.id)
      ),
      {
        id: "nordic_wood",
        name: "Nordic Wood",
        prompt: "light Scandinavian wood tones in birch, ash, or pale oak",
        category: "material",
        isDefault: true,
      },
      {
        id: "hygge_elements",
        name: "Hygge Elements",
        prompt: "cozy hygge touches with candles, textiles, and warm accents",
        category: "detail",
        isDefault: false,
      },
    ],
    cameraPresets: COMMON_CAMERA_PRESETS,
    lightingConfig: COMMON_LIGHTING_CONFIGS,
    tags: ["scandinavian", "nordic", "hygge", "light wood"],
  },

  // 5. Traditional
  {
    id: "traditional",
    category: "traditional",
    name: "Traditional Classic",
    description: "Shaker cabinets, crown molding, and timeless elegance",
    basePrompt:
      "Traditional classic kitchen with raised-panel Shaker cabinets, crown molding details, " +
      "marble countertops, subway tile backsplash, farmhouse sink, brass hardware, " +
      "pendant lighting over island, and warm white color palette. " +
      "Timeless elegance, photorealistic rendering, classic interior design.",
    styleModifiers: [
      ...COMMON_STYLE_MODIFIERS.filter((m) =>
        ["natural_materials", "cozy_warm", "styled_decor"].includes(m.id)
      ),
      {
        id: "classic_details",
        name: "Classic Details",
        prompt: "traditional millwork, corbels, and decorative moldings",
        category: "detail",
        isDefault: true,
      },
      {
        id: "antique_accents",
        name: "Antique Accents",
        prompt: "antique brass fixtures and vintage-inspired hardware",
        category: "material",
        isDefault: false,
      },
    ],
    cameraPresets: COMMON_CAMERA_PRESETS,
    lightingConfig: COMMON_LIGHTING_CONFIGS,
    tags: ["traditional", "classic", "shaker", "timeless"],
  },

  // 6. Kosher Family
  {
    id: "kosher_family",
    category: "kosher_family",
    name: "Kosher Family Kitchen",
    description: "Dual-zone layout with clear meat/dairy separation for observant families",
    basePrompt:
      "Large kosher family kitchen with clearly defined meat and dairy zones, " +
      "dual sinks with minimum 60cm separation, two sets of appliances, " +
      "color-coded storage areas, spacious island for family gathering, " +
      "modern traditional design, warm and welcoming atmosphere. " +
      "Functional kashrut-compliant layout, photorealistic rendering.",
    styleModifiers: [
      ...COMMON_STYLE_MODIFIERS.filter((m) =>
        ["natural_materials", "cozy_warm", "styled_decor", "bright_airy"].includes(m.id)
      ),
      {
        id: "dual_zones",
        name: "Dual Work Zones",
        prompt: "clearly marked dual work zones with visual separation",
        category: "composition",
        isDefault: true,
      },
      {
        id: "family_friendly",
        name: "Family Friendly",
        prompt: "family-friendly features with easy supervision and accessible storage",
        category: "detail",
        isDefault: false,
      },
    ],
    cameraPresets: COMMON_CAMERA_PRESETS,
    lightingConfig: COMMON_LIGHTING_CONFIGS,
    kashrut: {
      enabled: true,
      meatZonePrompt: "warm-toned meat preparation zone with red accent elements",
      dairyZonePrompt: "cool-toned dairy preparation zone with blue accent elements",
      separationDistance: 60,
    },
    tags: ["kosher", "family", "dual-zone", "kashrut"],
  },

  // 7. Compact
  {
    id: "compact",
    category: "compact",
    name: "Compact Efficient",
    description: "Space-saving solutions maximizing every square centimeter",
    basePrompt:
      "Compact efficient kitchen maximizing limited space with floor-to-ceiling storage, " +
      "pull-out pantry systems, multi-functional furniture, integrated appliances, " +
      "fold-down table, clever corner solutions, and light reflective surfaces. " +
      "Small space optimization, photorealistic rendering, smart design.",
    styleModifiers: [
      ...COMMON_STYLE_MODIFIERS.filter((m) =>
        ["high_gloss", "bright_airy", "minimal_clean"].includes(m.id)
      ),
      {
        id: "space_saving",
        name: "Space Saving",
        prompt: "innovative space-saving solutions and multi-functional elements",
        category: "detail",
        isDefault: true,
      },
      {
        id: "visual_expansion",
        name: "Visual Expansion",
        prompt: "design tricks to visually expand space with mirrors and light colors",
        category: "atmosphere",
        isDefault: false,
      },
    ],
    cameraPresets: COMMON_CAMERA_PRESETS,
    lightingConfig: COMMON_LIGHTING_CONFIGS,
    tags: ["compact", "small-space", "efficient", "apartment"],
  },

  // 8. Luxury
  {
    id: "luxury",
    category: "luxury",
    name: "Luxury Statement",
    description: "High-end materials, professional-grade appliances, and designer details",
    basePrompt:
      "Luxury statement kitchen with custom cabinetry, book-matched marble slabs, " +
      "professional-grade appliances, integrated wine storage, butler's pantry glimpse, " +
      "gold or bronze hardware, crystal pendant lighting, and exquisite craftsmanship. " +
      "Ultra-high-end design, photorealistic 8K rendering, luxury real estate photography.",
    styleModifiers: [
      ...COMMON_STYLE_MODIFIERS.filter((m) =>
        ["high_gloss", "mixed_metals", "moody_dramatic", "architectural_focus"].includes(m.id)
      ),
      {
        id: "premium_materials",
        name: "Premium Materials",
        prompt: "book-matched marble, exotic woods, and premium natural stone",
        category: "material",
        isDefault: true,
      },
      {
        id: "statement_lighting",
        name: "Statement Lighting",
        prompt: "designer statement lighting fixtures as focal points",
        category: "detail",
        isDefault: false,
      },
      {
        id: "chef_grade",
        name: "Chef-Grade Equipment",
        prompt: "professional chef-grade appliances and commercial-style range",
        category: "detail",
        isDefault: false,
      },
    ],
    cameraPresets: COMMON_CAMERA_PRESETS,
    lightingConfig: COMMON_LIGHTING_CONFIGS,
    tags: ["luxury", "high-end", "premium", "designer"],
  },
];

// =============================================================================
// Template Lookup Functions
// =============================================================================

/**
 * Get a template by ID or category
 */
export function getTemplate(idOrCategory: string): KitchenTemplate | undefined {
  return KITCHEN_TEMPLATES.find(
    (t) => t.id === idOrCategory || t.category === idOrCategory
  );
}

/**
 * Get all templates for a category
 */
export function getTemplatesByCategory(
  category: TemplateCategory
): KitchenTemplate[] {
  return KITCHEN_TEMPLATES.filter((t) => t.category === category);
}

/**
 * Get templates by tag
 */
export function getTemplatesByTag(tag: string): KitchenTemplate[] {
  return KITCHEN_TEMPLATES.filter((t) => t.tags?.includes(tag));
}

// =============================================================================
// Prompt Assembly (BYK-140 Core Function)
// =============================================================================

export interface PromptAssemblyOptions {
  template: KitchenTemplate;
  selectedModifiers?: string[]; // IDs of style modifiers to include
  camera?: CameraPreset;
  lighting?: LightingMood;
  customPrompt?: string;
  resolution?: RenderResolution;
  aspectRatio?: AspectRatio;
  enableKashrut?: boolean;
}

export interface AssembledPrompt {
  /** The complete assembled prompt for Gemini */
  prompt: string;
  /** Negative prompt (what to avoid) */
  negativePrompt: string;
  /** Resolution dimensions */
  dimensions: { width: number; height: number };
  /** Metadata for logging/debugging */
  metadata: {
    templateId: string;
    modifierIds: string[];
    cameraId: string;
    lightingId: string;
    hasCustomPrompt: boolean;
    kashrutEnabled: boolean;
  };
}

/**
 * Assemble a complete prompt from template and options
 *
 * Follows Gemini 3 Pro hierarchical reasoning:
 * Subject → Style → Camera → Lighting → Details
 */
export function assembleKitchenPrompt(
  options: PromptAssemblyOptions
): AssembledPrompt {
  const {
    template,
    selectedModifiers = [],
    camera = "corner",
    lighting = "daylight",
    customPrompt,
    resolution = "2K",
    aspectRatio = "16:9",
    enableKashrut = false,
  } = options;

  // 1. Start with base prompt (Subject)
  const parts: string[] = [template.basePrompt];

  // 2. Add selected style modifiers (Style)
  const allModifiers = template.styleModifiers;
  const activeModifierIds = selectedModifiers.length > 0
    ? selectedModifiers
    : allModifiers.filter((m) => m.isDefault).map((m) => m.id);

  const activeModifiers = allModifiers.filter((m) =>
    activeModifierIds.includes(m.id)
  );

  if (activeModifiers.length > 0) {
    parts.push(activeModifiers.map((m) => m.prompt).join(", "));
  }

  // 3. Add camera angle (Camera)
  const cameraConfig = template.cameraPresets.find((c) => c.id === camera);
  if (cameraConfig) {
    parts.push(cameraConfig.prompt);
  }

  // 4. Add lighting mood (Lighting)
  const lightingConfig = template.lightingConfig.find((l) => l.id === lighting);
  if (lightingConfig) {
    parts.push(lightingConfig.prompt);
  }

  // 5. Add kashrut-specific elements if enabled
  if (enableKashrut && template.kashrut?.enabled) {
    parts.push(template.kashrut.meatZonePrompt);
    parts.push(template.kashrut.dairyZonePrompt);
  }

  // 6. Add custom prompt at the end (User Details)
  if (customPrompt?.trim()) {
    parts.push(customPrompt.trim());
  }

  // Get resolution dimensions
  const resConfig = RESOLUTION_CONFIG[resolution];
  const dimensions = {
    width: resConfig.width,
    height: resConfig.height[aspectRatio],
  };

  return {
    prompt: parts.join(". "),
    negativePrompt:
      "blurry, low quality, distorted, unrealistic proportions, " +
      "watermark, text, signature, oversaturated, underexposed",
    dimensions,
    metadata: {
      templateId: template.id,
      modifierIds: activeModifierIds,
      cameraId: camera,
      lightingId: lighting,
      hasCustomPrompt: !!customPrompt?.trim(),
      kashrutEnabled: enableKashrut && !!template.kashrut?.enabled,
    },
  };
}

/**
 * Validate a template for completeness
 */
export function validateTemplate(
  template: KitchenTemplate
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!template.id) errors.push("Missing template ID");
  if (!template.name) errors.push("Missing template name");
  if (!template.basePrompt) errors.push("Missing base prompt");
  if (!template.cameraPresets?.length) errors.push("No camera presets defined");
  if (!template.lightingConfig?.length) errors.push("No lighting configs defined");

  return {
    valid: errors.length === 0,
    errors,
  };
}
